import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { supabase } from './lib/supabase';
import { createDeck, createIndexedData, getDeckStats } from './utils/deckUtils';
import { DECKS } from './data/deckConfig';
import { useSound } from './hooks/useSound';
import { useAuth } from './hooks/useAuth';
import Paywall from './components/Paywall';
import FreeLimitModal from './components/FreeLimitModal';
import StreakCelebration from './components/StreakCelebration';
import AuthForm from './components/AuthForm';
import FlashCard from './components/FlashCard';
import MyCardsModal from './components/MyCardsModal';
import SaveProgressModal from './components/SaveProgressModal';
import {
  loadGuestProgress,
  saveGuestProgress,
  countGuestCards,
  isSavePromptDismissed,
  dismissSavePrompt,
  migrateGuestProgressToAccount,
} from './utils/guestProgress';

// How many cards a guest works through before we ask them to keep the progress.
const SAVE_PROMPT_AFTER_CARDS = 12;

const AppContent = () => {
  const { isPro, loginUser, logoutUser } = useSubscription();

  // Current deck selection
  const [currentDeckId, setCurrentDeckId] = useState('characters');

  // Custom cards state
  const [customCards, setCustomCards] = useState([]);

  // Per-deck progress tracking
  // Structure: { [deckId]: { known: Set, learning: Set } }
  const [deckProgress, setDeckProgress] = useState({});

  // Get current deck's known/learning sets
  const knownChars = deckProgress[currentDeckId]?.known || new Set();
  const learningChars = deckProgress[currentDeckId]?.learning || new Set();

  // Helper to update progress for current deck
  const setKnownChars = useCallback((updater) => {
    setDeckProgress(prev => {
      const current = prev[currentDeckId] || { known: new Set(), learning: new Set() };
      const newKnown = typeof updater === 'function' ? updater(current.known) : updater;
      return {
        ...prev,
        [currentDeckId]: { ...current, known: newKnown }
      };
    });
  }, [currentDeckId]);

  const setLearningChars = useCallback((updater) => {
    setDeckProgress(prev => {
      const current = prev[currentDeckId] || { known: new Set(), learning: new Set() };
      const newLearning = typeof updater === 'function' ? updater(current.learning) : updater;
      return {
        ...prev,
        [currentDeckId]: { ...current, learning: newLearning }
      };
    });
  }, [currentDeckId]);

  // Callback for when user progress is loaded (now receives per-deck data).
  // Merges rather than replaces: a guest may have swiped before signing in, and
  // that work is already in state by the time the account's rows arrive.
  // Where the two disagree on a card, the stored row wins — it's the persisted truth.
  const handleUserProgressLoad = useCallback((progressByDeck) => {
    setDeckProgress(prev => {
      const next = { ...prev };

      Object.entries(progressByDeck).forEach(([deckId, incoming]) => {
        const current = next[deckId] || { known: new Set(), learning: new Set() };

        const known = new Set([...current.known, ...incoming.known]);
        const learning = new Set([...current.learning, ...incoming.learning]);

        incoming.known.forEach(index => learning.delete(index));
        incoming.learning.forEach(index => known.delete(index));

        next[deckId] = { known, learning };
      });

      return next;
    });
  }, []);

  // Auth hook
  // Note: useAuth also exposes handleSignInWithGoogle, but no UI calls it yet —
  // the Google provider needs confirming in Supabase before a button ships.
  const {
    user,
    guestMode,
    authLoading,
    authMode,
    setAuthMode,
    authError,
    email,
    setEmail,
    password,
    setPassword,
    handleSignUp,
    handleSignIn,
    handleSignOut: authSignOut,
  } = useAuth(handleUserProgressLoad);

  const [showPaywall, setShowPaywall] = useState(false);
  const [showFreeLimitModal, setShowFreeLimitModal] = useState(false);

  // Auth is a screen the user opens, not a gate they land on.
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  const openAuthScreen = useCallback((mode) => {
    setAuthMode(mode);
    setShowSavePrompt(false);
    setShowAuthScreen(true);
  }, [setAuthMode]);

  // Close the auth screen automatically once sign-in succeeds.
  useEffect(() => {
    if (user) setShowAuthScreen(false);
  }, [user]);

  // --- Guest progress -------------------------------------------------------

  const guestHydratedRef = useRef(false);
  const guestMigratedRef = useRef(false);

  // Restore anything a guest built up in a previous visit.
  useEffect(() => {
    if (authLoading || user || !guestMode || guestHydratedRef.current) return;
    guestHydratedRef.current = true;

    const stored = loadGuestProgress();
    if (stored) {
      setDeckProgress(prev => ({ ...stored, ...prev }));
    }
  }, [authLoading, user, guestMode]);

  // Keep localStorage in step so a refresh doesn't cost them the session.
  useEffect(() => {
    if (!guestMode || user || !guestHydratedRef.current) return;
    saveGuestProgress(deckProgress);
  }, [deckProgress, guestMode, user]);

  // On first authentication, move the guest's work into their account.
  useEffect(() => {
    if (!user || guestMode || guestMigratedRef.current) return;
    guestMigratedRef.current = true;

    migrateGuestProgressToAccount(user.id).then(count => {
      if (count > 0) {
        console.info(`Moved ${count} guest cards into the new account.`);
      }
    });
  }, [user, guestMode]);

  // Ask a guest to keep their progress once there's enough of it to matter.
  useEffect(() => {
    if (!guestMode || user || showSavePrompt || isSavePromptDismissed()) return;

    if (countGuestCards(deckProgress) >= SAVE_PROMPT_AFTER_CARDS) {
      setShowSavePrompt(true);
    }
  }, [deckProgress, guestMode, user, showSavePrompt]);

  // Reset scroll position when auth state changes
  useEffect(() => {
    window.scrollTo(0, 0);
    const t1 = setTimeout(() => window.scrollTo(0, 0), 100);
    const t2 = setTimeout(() => window.scrollTo(0, 0), 300);
    const t3 = setTimeout(() => window.scrollTo(0, 0), 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [user, guestMode]);

  // Load custom cards from Supabase
  const loadCustomCards = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('custom_cards')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        // Table might not exist yet - that's ok
        if (!error.message?.includes('does not exist')) {
          console.error('Error loading custom cards:', error);
        }
        return;
      }

      if (data) {
        setCustomCards(data.map(card => ({
          id: card.id,
          char: card.char,
          pinyin: card.pinyin,
          meaning: card.meaning,
          example: card.example || '',
          createdAt: card.created_at,
        })));
      }
    } catch (err) {
      console.error('Error loading custom cards:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user && !guestMode) {
      loadCustomCards();
    }
  }, [user, guestMode, loadCustomCards]);

  const handleAddCustomCard = async (card) => {
    if (!user || guestMode) return;

    try {
      const { data, error } = await supabase
        .from('custom_cards')
        .insert({
          user_id: user.id,
          char: card.char,
          pinyin: card.pinyin,
          meaning: card.meaning,
          example: card.example || '',
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding custom card:', error);
        return;
      }

      const newCard = {
        id: data.id,
        char: data.char,
        pinyin: data.pinyin,
        meaning: data.meaning,
        example: data.example || '',
        createdAt: data.created_at,
      };

      setCustomCards(prev => [newCard, ...prev]);

      // Rebuild deck if on characters (to include new custom card)
      if (currentDeckId === 'characters') {
        setDeck(createDeck('characters', isPro, [...customCards, newCard]));
      }
    } catch (err) {
      console.error('Error adding custom card:', err);
    }
  };

  const handleDeleteCustomCard = async (cardId) => {
    if (!user || guestMode) return;

    if (!window.confirm('Delete this custom card?')) return;

    try {
      const { error } = await supabase
        .from('custom_cards')
        .delete()
        .eq('id', cardId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting custom card:', error);
        return;
      }

      const updatedCards = customCards.filter(c => c.id !== cardId);
      setCustomCards(updatedCards);

      // Rebuild deck if on characters
      if (currentDeckId === 'characters') {
        setDeck(createDeck('characters', isPro, updatedCards));
      }
    } catch (err) {
      console.error('Error deleting custom card:', err);
    }
  };

  // Flashcard deck state
  const [deck, setDeck] = useState(() => createDeck('characters', isPro, []));

  // Effect to update deck when isPro or currentDeckId changes
  useEffect(() => {
    if (currentDeckId === 'characters') {
      setDeck(createDeck('characters', isPro, customCards));
    } else {
      setDeck(createDeck(currentDeckId, isPro, []));
    }
    setCurrentIndex(0);
    setIsFlipped(false);
  }, [isPro, currentDeckId, customCards]);

  const handleDeckChange = (newDeckId) => {
    setCurrentDeckId(newDeckId);
    setCurrentIndex(0);
    setIsFlipped(false);
    setStreak(0);
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [streak, setStreak] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(null);
  const [showMyCards, setShowMyCards] = useState(false);

  // Theme state
  // Light ("paper") is the default; the choice is remembered per device.
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });

  // Themes are CSS-only: this flips one attribute and every token swaps.
  // Nothing below branches on isDarkMode.
  useEffect(() => {
    const mode = isDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', mode);
    localStorage.setItem('theme', mode);
  }, [isDarkMode]);

  const cardRef = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);

  const userRef = useRef(user);
  const guestModeRef = useRef(guestMode);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    guestModeRef.current = guestMode;
  }, [guestMode]);

  const { playCorrect, playStreak, playWrong } = useSound();

  // Sync user with RevenueCat for cross-platform subscriptions
  useEffect(() => {
    if (user?.id && !guestMode) {
      loginUser(user.id);
    }
  }, [user?.id, guestMode, loginUser]);

  const handleSignOut = async () => {
    await authSignOut();
    await logoutUser();
    setStreak(0);
    setDeckProgress({});
    setCustomCards([]);
  };

  // Save progress to database
  const saveProgress = useCallback(async (charIndex, isKnown) => {
    const currentUser = userRef.current;
    const isGuestMode = guestModeRef.current;

    if (!currentUser || isGuestMode) return;

    try {
      // Conflict target must be the full (user, deck, card) triple. Keying on
      // (user_id, character_index) alone made decks share rows: swiping
      // Radicals #5 rewrote the Characters #5 row instead of creating its own.
      const { error } = await supabase.from('user_progress').upsert({
        user_id: currentUser.id,
        character_index: charIndex,
        deck_id: currentDeckId,
        known: isKnown,
        last_seen: new Date().toISOString()
      }, {
        onConflict: 'user_id,deck_id,character_index'
      });

      // supabase-js resolves rather than throws on PostgREST errors, so this
      // has to be checked explicitly or failures are invisible.
      if (error) {
        console.error('Error saving progress:', error.code, error.message);
      }
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  }, [currentDeckId]);

  const currentCard = deck[currentIndex];

  // Progress is measured against the number of *distinct* cards the user can
  // reach, not deck.length — the deck holds weighted duplicates (a T1 character
  // appears 6 times), so dividing by it capped the bar at ~18%.
  const uniqueCardCount = currentDeckId === 'custom'
    ? customCards.length
    : getDeckStats(currentDeckId, isPro).available
      + (currentDeckId === 'characters' ? customCards.length : 0);

  const progress = uniqueCardCount > 0
    ? Math.min(100, ((knownChars.size + learningChars.size) / uniqueCardCount) * 100)
    : 0;

  // Get current deck config
  const currentDeckConfig = DECKS[currentDeckId];

  // Get indexed data for progress display.
  // Memoized: this allocates one object per card, and every drag frame
  // re-renders this component.
  const currentDeckData = useMemo(
    () => createIndexedData(currentDeckId),
    [currentDeckId]
  );

  const handleReshuffle = useCallback(() => {
    if (currentDeckId === 'characters') {
      setDeck(createDeck('characters', isPro, customCards));
    } else {
      setDeck(createDeck(currentDeckId, isPro, []));
    }
    setCurrentIndex(0);
    setStreak(0);
    setIsFlipped(false);
  }, [isPro, currentDeckId, customCards]);

  const handleSwipe = useCallback((direction) => {
    if (!currentCard) return;

    setSwipeDirection(direction);
    const charIndex = currentCard.originalIndex;

    // Save progress to database (skip for custom cards in local state)
    if (currentCard.deckId !== 'custom') {
      saveProgress(charIndex, direction === 'right');
    }

    // Update character lists
    if (direction === 'right') {
      setKnownChars(prev => new Set([...prev, charIndex]));
      setLearningChars(prev => {
        const next = new Set(prev);
        next.delete(charIndex);
        return next;
      });
    } else {
      setLearningChars(prev => new Set([...prev, charIndex]));
      setKnownChars(prev => {
        const next = new Set(prev);
        next.delete(charIndex);
        return next;
      });
    }

    setTimeout(() => {
      if (direction === 'right') {
        const newStreak = streak + 1;
        setStreak(newStreak);

        if (newStreak === 10 || newStreak === 50 || newStreak === 100) {
          playStreak(newStreak);
          setShowStreakCelebration(newStreak);
        } else {
          playCorrect();
        }
      } else {
        setStreak(0);
        playWrong();
      }

      setSwipeDirection(null);
      setIsFlipped(false);
      setDragX(0);

      if (currentIndex < deck.length - 1) {
        setCurrentIndex(i => i + 1);
      } else {
        handleReshuffle();

        // Reaching the end of the free deck is the moment the limit is real,
        // which beats interrupting someone one second after they arrive.
        if (!isPro && !sessionStorage.getItem('seenFreeLimitModal')) {
          sessionStorage.setItem('seenFreeLimitModal', 'true');
          setShowFreeLimitModal(true);
        }
      }
    }, 300);
  }, [currentCard, currentIndex, deck.length, streak, isPro, playCorrect, playStreak, playWrong, handleReshuffle, saveProgress, setKnownChars, setLearningChars]);

  const handleResetProgress = async () => {
    if (!window.confirm(`Reset all progress for ${currentDeckConfig?.name || 'this deck'}? This cannot be undone.`)) {
      return;
    }

    if (user && !guestMode) {
      await supabase
        .from('user_progress')
        .delete()
        .eq('user_id', user.id)
        .eq('deck_id', currentDeckId);
    }

    setDeckProgress(prev => ({
      ...prev,
      [currentDeckId]: { known: new Set(), learning: new Set() }
    }));

    handleReshuffle();
  };

  const handlePointerDown = (e) => {
    setIsDragging(true);
    startX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    startY.current = e.clientY || e.touches?.[0]?.clientY || 0;
    startTime.current = Date.now();
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    if (e.cancelable) e.preventDefault();
    const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
    const diff = currentX - startX.current;
    setDragX(diff);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;

    const endX = e.clientX || e.changedTouches?.[0]?.clientX || 0;
    const endY = e.clientY || e.changedTouches?.[0]?.clientY || 0;
    const diffX = endX - startX.current;
    const diffY = endY - startY.current;
    const elapsed = Date.now() - startTime.current;

    setIsDragging(false);

    if (Math.abs(diffX) < 22 && Math.abs(diffY) < 22 && elapsed < 500) {
      setIsFlipped(f => !f);
      setDragX(0);
      return;
    }

    if (Math.abs(diffX) > 100) {
      handleSwipe(diffX > 0 ? 'right' : 'left');
    } else {
      setDragX(0);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (swipeDirection) return;

      // Don't hijack keys while typing — space and arrows belong to the field.
      const target = e.target;
      if (
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName)
      ) {
        return;
      }

      // Only drive the card when the card is actually the visible surface.
      if (!user && !guestMode) return;
      if (showPaywall || showFreeLimitModal || showMyCards) return;

      if (e.key === 'ArrowRight') {
        handleSwipe('right');
      } else if (e.key === 'ArrowLeft') {
        handleSwipe('left');
      } else if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsFlipped(f => !f);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [swipeDirection, handleSwipe, user, guestMode, showPaywall, showFreeLimitModal, showMyCards]);

  // Loading state
  if (authLoading) {
    return (
      <div className="app-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="rp-text">Loading…</div>
      </div>
    );
  }

  // Auth screen — reached deliberately from the header or the save prompt,
  // never the state a first-time visitor lands on.
  if (showAuthScreen && !user) {
    return (
      <AuthForm
        authMode={authMode}
        setAuthMode={setAuthMode}
        authError={authError}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        handleSignIn={handleSignIn}
        handleSignUp={handleSignUp}
        onClose={() => setShowAuthScreen(false)}
        guestCardCount={countGuestCards(deckProgress)}
      />
    );
  }

  const myCardsModal = showMyCards && (
    <MyCardsModal
      onClose={() => setShowMyCards(false)}
      currentDeckId={currentDeckId}
      onDeckChange={(deckId) => {
        handleDeckChange(deckId);
        setShowMyCards(false);
      }}
      knownChars={knownChars}
      learningChars={learningChars}
      isPro={isPro}
      isAuthenticated={!!user && !guestMode}
      customCards={customCards}
      onAddCustomCard={handleAddCustomCard}
      onDeleteCustomCard={handleDeleteCustomCard}
      onResetProgress={handleResetProgress}
      deckData={currentDeckData}
    />
  );

  // No cards in deck
  if (deck.length === 0 || !currentCard) {
    return (
      <div
        className="app-root"
        style={{ alignItems: 'center', justifyContent: 'center', gap: '18px', textAlign: 'center' }}
      >
        <div className="rp-logo-tile" aria-hidden="true">字</div>
        <h2 className="rp-title">No cards here yet</h2>
        <p className="rp-text" style={{ maxWidth: '34ch' }}>
          {currentDeckId === 'custom'
            ? 'Add a few of your own characters to start this deck.'
            : 'This deck is empty, or the rest of it needs Pro.'}
        </p>
        <button className="rp-btn rp-btn-lime" style={{ width: 'auto' }} onClick={() => setShowMyCards(true)}>
          {currentDeckId === 'custom' ? 'Add custom cards' : 'Choose another deck'}
        </button>

        {myCardsModal}
      </div>
    );
  }

  return (
    <div className="app-root">
      {showStreakCelebration && (
        <StreakCelebration
          milestone={showStreakCelebration}
          onComplete={() => setShowStreakCelebration(null)}
        />
      )}

      {myCardsModal}

      {/* TOP: header + progress */}
      <div className="top-section">
        <div className="app-header">
          {/* The streak lives here, with the other stats — it is not an action,
              and putting it in the button row made that row wrap onto a second
              line the moment a streak started, shifting the whole card down. */}
          <div className="header-stats">
            <span className="header-count-yes">{knownChars.size}</span>
            <span className="header-stat-label">记住</span>
            <span className="header-count-no">{learningChars.size}</span>
            <span className="header-stat-label">学习中</span>
            {streak > 0 && (
              <span className="header-streak" title={`${streak} in a row`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2s5 4.5 5 9a5 5 0 0 1-10 0c0-1.3.4-2.4 1-3.4C8 9.9 9 11 10 11c0-3 2-6 2-9Z"/></svg>
                {streak}
              </span>
            )}
            {guestMode && <span className="header-guest">(Guest)</span>}
          </div>

          <div className="header-buttons">
            {user && (
              <button onClick={handleSignOut} className="header-btn" title="Sign out">
                <span className="header-btn-text">Sign out</span>
                <span className="header-btn-icon" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/></svg>
                </span>
              </button>
            )}

            {guestMode && (
              <button onClick={() => openAuthScreen('login')} className="header-btn" title="Sign in">
                <span className="header-btn-text">Sign in</span>
                <span className="header-btn-icon" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="m10 17 5-5-5-5"/><path d="M15 12H3"/></svg>
                </span>
              </button>
            )}

            <button
              onClick={() => setShowMyCards(true)}
              className="header-btn header-btn-deck"
              title={`Deck: ${currentDeckConfig?.name || 'Characters'}`}
            >
              <span className="header-btn-text">{currentDeckConfig?.name || 'Deck'}</span>
              <span className="header-btn-icon" style={{ fontFamily: 'var(--hanzi)' }}>
                {currentDeckConfig?.icon || '字'}
              </span>
            </button>

            <button onClick={handleReshuffle} className="header-btn" title="Reshuffle deck">
              <span className="header-btn-text">Reshuffle</span>
              <span className="header-btn-icon" aria-hidden="true">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m18 14 4 4-4 4"/><path d="m18 2 4 4-4 4"/><path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"/><path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"/><path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"/></svg>
              </span>
            </button>

            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="header-btn"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3a6.4 6.4 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
              )}
            </button>

            <div className="header-btn header-btn-static" title="Position in deck">
              {currentIndex + 1}/{deck.length}
            </div>
          </div>
        </div>

        <div
          className="progress-bar-container"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${currentDeckConfig?.name || 'Deck'} progress`}
        >
          <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* MIDDLE: card + actions */}
      <div className="middle-section">
        <div className="card-stack">
          {/* Cards behind the top one: offset straight down and tapered in,
              so they read as a stack rather than as misaligned corners. */}
          {[2, 1].map((offset) => (
            <div
              key={offset}
              className="card-stack-shadow"
              style={{
                transform: `translateY(${offset * 9}px) scaleX(${1 - offset * 0.035})`,
              }}
            />
          ))}

          <FlashCard
            ref={cardRef}
            currentCard={currentCard}
            currentIndex={currentIndex}
            deckLength={deck.length}
            isFlipped={isFlipped}
            isDragging={isDragging}
            dragX={dragX}
            swipeDirection={swipeDirection}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={() => { if (isDragging) { setIsDragging(false); setDragX(0); } }}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
          />
        </div>

        <div className="action-buttons">
          <button className="action-btn action-btn-no" onClick={() => handleSwipe('left')} aria-label="Still learning">
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--no-ink)" strokeWidth="3.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>

          <button className="action-btn action-btn-yes" onClick={() => handleSwipe('right')} aria-label="I know this">
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--yes-ink)" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
          </button>
        </div>
      </div>

      {/* BOTTOM: hint + Pro */}
      <div className="bottom-section">
        <div className="instructions-text">← 再学习 &nbsp;·&nbsp; 点击翻转 &nbsp;·&nbsp; 记住了 →</div>

        {!isPro && (
          <button className="pro-unlock-btn" onClick={() => setShowPaywall(true)}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 6l4.5 3.5L12 3l4.5 6.5L21 6l-1.8 12H4.8L3 6Z"/></svg>
            Unlock Pro
          </button>
        )}
      </div>

      {showSavePrompt && (
        <SaveProgressModal
          cardCount={countGuestCards(deckProgress)}
          onCreateAccount={() => openAuthScreen('signup')}
          onSignIn={() => openAuthScreen('login')}
          onDismiss={() => {
            dismissSavePrompt();
            setShowSavePrompt(false);
          }}
        />
      )}

      {showFreeLimitModal && (
        <FreeLimitModal
          onClose={() => setShowFreeLimitModal(false)}
          onUpgrade={() => {
            setShowFreeLimitModal(false);
            setShowPaywall(true);
          }}
        />
      )}

      {showPaywall && <Paywall onClose={() => setShowPaywall(false)} />}
    </div>
  );
};

export default function ChineseFlashcards() {
  return (
    <SubscriptionProvider>
      <AppContent />
    </SubscriptionProvider>
  );
}
